/**
 * Dialog tạo/sửa nguồn khách hàng.
 * Hủy hỏi xác nhận; nút X và Esc đóng thẳng; backdrop không đóng.
 */
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { queryKeys } from '@/shared/query';
import { getApiErrorDetails } from '@/shared/utils/apiError';
import {
  customerSourceAPI,
  type CustomerSource,
} from '@/modules/patient/api/customerSource.api';
import CustomerSourceFormFields from './CustomerSourceFormFields';
import {
  buildCustomerSourceFormSchema,
  customerSourceToFormValues,
  emptyCustomerSourceFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
  type CustomerSourceFormValues,
} from '../utils/customerSourceForm';

export interface CustomerSourceFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = tạo mới. */
  editing?: CustomerSource | null;
  /** Called after a successful create, before the dialog closes. */
  onCreated?: (source: CustomerSource) => void;
}

const CustomerSourceFormDialog: React.FC<CustomerSourceFormDialogProps> = ({
  open,
  onClose,
  editing = null,
  onCreated,
}) => {
  const { t } = useTranslation('patient');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const isEdit = Boolean(editing);

  const schema = useMemo(() => buildCustomerSourceFormSchema(t), [t]);
  const form = useForm<CustomerSourceFormValues>({
    defaultValues: emptyCustomerSourceFormValues(),
    resolver: yupResolver(schema) as unknown as Resolver<CustomerSourceFormValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (!open) return;
    reset(editing ? customerSourceToFormValues(editing) : emptyCustomerSourceFormValues());
  }, [open, editing, reset]);

  const invalidateSources = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.patient.all });

  const saveMutation = useMutation({
    mutationFn: async (values: CustomerSourceFormValues) => {
      if (isEdit && editing) {
        return customerSourceAPI.update(editing.id, formValuesToUpdatePayload(values));
      }
      return customerSourceAPI.create(formValuesToCreatePayload(values));
    },
    onSuccess: async (res) => {
      toast.success(
        isEdit
          ? t('customerSourceCatalog.toasts.updated')
          : t('customerSourceCatalog.toasts.created'),
      );
      await invalidateSources();
      if (!isEdit && res.data) onCreated?.(res.data);
      onClose();
    },
    onError: (err) => {
      toast.error(
        getApiErrorDetails(err, t('customerSourceCatalog.toasts.saveFailed')).message,
      );
    },
  });

  const busy = saveMutation.isPending;
  const title = isEdit
    ? t('customerSourceCatalog.dialogs.editTitle')
    : t('customerSourceCatalog.dialogs.addTitle');

  return (
    <FormDialog
      open={open}
      title={title}
      busy={busy}
      onClose={onClose}
      minHeight={400}
      contentSx={{ '&&': { pt: 2.5 } }}
      onSubmit={() => void handleSubmit((values) => saveMutation.mutate(values))()}
      submitLabel={tCommon('save')}
      busySubmitLabel={tCommon('saving')}
    >
      <FormProvider {...form}>
        <CustomerSourceFormFields
          isEdit={isEdit}
          code={editing?.code}
          disabled={busy}
        />
      </FormProvider>
    </FormDialog>
  );
};

export default CustomerSourceFormDialog;
